import api from './api';

// ── Mappers ─────────────────────────────────────────────

/**
 * Map API expense (snake_case) → Frontend expense (camelCase)
 */
function mapExpense(e) {
  return {
    id: e.id,
    description: e.description,
    amount: parseFloat(e.amount),
    expenseDate: e.expense_date,
    category: e.category?.name || '',
    categoryId: e.category?.id,
    categoryColor: e.category?.color,
    receiptPath: e.receipt_path,
    status: e.status,
    notes: e.notes,
    userName: e.created_by?.name || '',
    userId: e.created_by?.id,
    rejectionReason: e.rejection_reason,
    createdAt: e.created_at,
    updatedAt: e.updated_at,
  };
}

/**
 * Map Frontend expense (camelCase) → API expense (snake_case)
 */
function mapExpenseToApi(data) {
  return {
    description: data.description,
    amount: data.amount,
    expense_date: data.expenseDate,
    category_id: data.categoryId || data.category_id,
    notes: data.notes,
  };
}

/**
 * Map frontend filter params → API query params
 */
function mapFilters(params) {
  const mapped = {};

  if (params.page) mapped.page = params.page;
  if (params.pageSize) mapped.per_page = params.pageSize;
  if (params.status) mapped.status = params.status;
  if (params.category) mapped.category_id = params.category;
  if (params.dateFrom) mapped.date_from = params.dateFrom;
  if (params.dateTo) mapped.date_to = params.dateTo;
  if (params.search) mapped.search = params.search;

  return mapped;
}

// ── Service functions ───────────────────────────────────

/**
 * Get all expenses with optional filters
 * @param {Object} params - Query parameters
 * @returns {Promise} { data, total, page, pageSize }
 */
export const getExpenses = async (params = {}) => {
  try {
    const response = await api.get('/expenses', { params: mapFilters(params) });
    const { data, meta } = response.data;

    return {
      data: data.map(mapExpense),
      total: meta?.total || data.length,
      page: meta?.current_page || 1,
      pageSize: meta?.per_page || 10,
    };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao buscar despesas.');
  }
};

/**
 * Get expense by ID
 * @param {number} id
 * @returns {Promise} Expense data
 */
export const getExpenseById = async (id) => {
  try {
    const response = await api.get(`/expenses/${id}`);
    return mapExpense(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao buscar despesa.');
  }
};

/**
 * Create new expense
 * @param {Object} expenseData
 * @returns {Promise} Created expense
 */
export const createExpense = async (expenseData) => {
  try {
    const response = await api.post('/expenses', mapExpenseToApi(expenseData));
    return mapExpense(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao criar despesa.');
  }
};

/**
 * Update expense
 * @param {number} id
 * @param {Object} expenseData
 * @returns {Promise} Updated expense
 */
export const updateExpense = async (id, expenseData) => {
  try {
    const response = await api.put(`/expenses/${id}`, mapExpenseToApi(expenseData));
    return mapExpense(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao atualizar despesa.');
  }
};

/**
 * Delete expense
 * @param {number} id
 * @returns {Promise}
 */
export const deleteExpense = async (id) => {
  try {
    await api.delete(`/expenses/${id}`);
    return { success: true };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao deletar despesa.');
  }
};

/**
 * Approve expense
 * @param {number} id
 * @param {string} notes
 * @returns {Promise}
 */
export const approveExpense = async (id, notes = '') => {
  try {
    const response = await api.post(`/expenses/${id}/approve`, { notes });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao aprovar despesa.');
  }
};

/**
 * Reject expense
 * @param {number} id
 * @param {string} notes - Rejection reason
 * @returns {Promise}
 */
export const rejectExpense = async (id, notes) => {
  try {
    const response = await api.post(`/expenses/${id}/reject`, { rejection_reason: notes });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao rejeitar despesa.');
  }
};

/**
 * Upload receipt for an expense
 * @param {number} expenseId
 * @param {File} file - The file to upload
 * @returns {Promise} Upload result
 */
export const uploadReceipt = async (expenseId, file) => {
  try {
    const formData = new FormData();
    formData.append('receipt', file);

    const response = await api.post(`/expenses/${expenseId}/receipt`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao enviar comprovante.');
  }
};

/**
 * Download receipt for an expense
 * @param {number} expenseId
 * @param {string} originalName - Original filename for the download
 * @returns {Promise}
 */
export const downloadReceipt = async (expenseId, originalName) => {
  try {
    const response = await api.get(`/expenses/${expenseId}/receipt`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = originalName || 'comprovante';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao baixar comprovante.');
  }
};

/**
 * Get expenses statistics for Dashboard
 * Uses the expenses endpoint (summary) + transparency/summary (byCategory)
 * @returns {Promise} Stats data
 */
export const getExpensesStats = async () => {
  try {
    // Fetch summary from expenses endpoint and transparency in parallel
    const [expensesRes, transparencyRes] = await Promise.all([
      api.get('/expenses', { params: { per_page: 1 } }),
      api.get('/transparency/summary'),
    ]);

    const summary = expensesRes.data.summary || {};
    const transparency = transparencyRes.data.data || transparencyRes.data || {};

    // Build byCategory from transparency data
    const byCategory = {};
    if (transparency.by_category) {
      transparency.by_category.forEach((cat) => {
        const name = cat.category?.name || cat.name || cat.category || 'Sem categoria';
        byCategory[name] = parseFloat(cat.total || cat.amount || 0);
      });
    }

    return {
      totalApproved: parseFloat(summary.total_amount || 0),
      totalPending: parseFloat(summary.pending_amount || 0),
      countPending: summary.pending_count || 0,
      countApproved: summary.approved_count || 0,
      totalCurrentMonth: parseFloat(transparency.total_current_month || summary.total_amount || 0),
      byCategory,
    };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao buscar estatísticas.');
  }
};
